import type { FC } from 'react';
import { useI18n, useUrl } from '@packages/shared';
import { LanguageSwitch32 } from '@packages/themes/icons';
import type { CookieOptions } from '@packages/utilities';
import { useConfig } from '@packages/utilities';
import { getFullLocaleFromConfig, localizedPathnameCacheKey } from '@packages/config/src/default';
import { type Language } from '@packages/config';
import useSWR from 'swr';
import { setCookie } from 'nookies';
import type { SlotProps } from './Slot';
import { Slot } from './Slot';

export type LocaleSwitchSlotProps = Pick<SlotProps, 'sx'>;

/** Locale Switch slot with list of helpers in popover */
export const LocaleSwitchSlot: FC<LocaleSwitchSlotProps> = ({ sx }) => {
  const config = useConfig();
  const {
    i18n: { languages },
  } = useConfig();
  const { language } = useI18n();
  const url = useUrl();

  const { data: localizedPathname } = useSWR(localizedPathnameCacheKey, {
    // prevent fetching newer data and use only stale data (https://swr.vercel.app/docs/revalidation#disable-automatic-revalidations)
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const COOKIE_OPTIONS: CookieOptions = {
    path: '/',
    maxAge: 86400, // 90 days in seconds
    secure: false, // true would be nice but this is what intershop defines,
    sameSite: 'lax',
    httpOnly: false,
  };

  const href = localizedPathname || url;

  const targetLanguage: Language = languages.find((l: string) => l !== language)!;

  return (
    <Slot
      icon={<LanguageSwitch32 />}
      title="fr / de"
      locale={targetLanguage}
      href={href}
      onClick={() => {
        setCookie(null, 'PreferredLanguage', targetLanguage, COOKIE_OPTIONS);
        // Set PreferredLocale with legacy parameters for consistent behaviour with the legacy checkout.
        // Can be removed after nextjs checkout is live
        setCookie(
          null,
          'PreferredLocale',
          getFullLocaleFromConfig(targetLanguage, config).replace('-', '_'),
          {
            ...COOKIE_OPTIONS,
            domain: `.${window.document.domain}`,
          },
        );
      }}
      sx={sx}
    />
  );
};
